import { useMemo, useState } from "react";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Circle, MapPin, Phone, User as UserIcon, Loader2, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Onboarding checklist for demandeurs.
 * Shown until profile is complete (phone + city + address + postal_code).
 * Once complete, collapses into a discreet "profile complete" line.
 */
export default function DemandeurProfileChecklist() {
  const { user, refresh } = useAuth();

  const items = useMemo(() => [
    { key: "name", label: "Nom complet", icon: UserIcon, filled: !!user?.name?.trim() },
    { key: "phone", label: "Téléphone", icon: Phone, filled: !!user?.phone?.trim() },
    { key: "address", label: "Adresse (rue + n°)", icon: MapPin, filled: !!user?.address?.trim() },
    { key: "postal_code", label: "Code postal", icon: MapPin, filled: !!user?.postal_code?.trim() },
    { key: "city", label: "Ville", icon: MapPin, filled: !!user?.city?.trim() },
  ], [user]);

  const filledCount = items.filter((i) => i.filled).length;
  const total = items.length;
  const isComplete = filledCount === total;
  const percent = Math.round((filledCount / total) * 100);

  const [open, setOpen] = useState(!isComplete);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    address: user?.address || "",
    postal_code: user?.postal_code || "",
    city: user?.city || "",
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if ((v || "").trim() && (v || "").trim() !== (user?.[k] || "")) payload[k] = v.trim();
      });
      if (Object.keys(payload).length === 0) {
        toast.info("Aucune modification");
      } else {
        await apiClient.patch("/users/me", payload);
        toast.success("Profil mis à jour ✅");
        try { await refresh?.(); } catch (_e) { /* ignore */ }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setBusy(false);
    }
  };

  // Collapsed success state
  if (isComplete && !open) {
    return (
      <div
        className="mb-8 border-2 border-emerald-700 bg-emerald-50 p-4 flex items-center gap-3"
        data-testid="demandeur-profile-summary"
      >
        <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="display text-lg text-emerald-900">Profil complet — vous êtes prêt à publier des missions</div>
          <div className="mono text-[10px] uppercase tracking-widest text-emerald-800 mt-0.5">
            {user?.name} · {user?.phone} · {user?.address ? `${user.address}, ` : ""}{user?.postal_code} {user?.city}
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="mono text-[10px] uppercase tracking-widest border border-emerald-700 text-emerald-900 px-3 py-1.5 hover:bg-emerald-700 hover:text-white transition"
          data-testid="demandeur-profile-edit"
        >
          Modifier
        </button>
      </div>
    );
  }

  const borderColor = isComplete ? "border-emerald-700" : "border-[#FF4D00]";
  const bgColor = isComplete ? "bg-emerald-50" : "bg-[#FFF7ED]";
  const barColor = isComplete ? "bg-emerald-600" : "bg-[#FF4D00]";

  return (
    <div
      className={`mb-8 border-2 ${borderColor} ${bgColor} p-6`}
      data-testid="demandeur-profile-checklist"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-10 h-10 border-2 border-[#0A0A0B] ${barColor} flex items-center justify-center shrink-0`}>
            <UserIcon className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className={`mono text-[10px] uppercase tracking-widest font-semibold ${isComplete ? "text-emerald-700" : "text-[#FF4D00]"}`}>
              // Onboarding — Profil demandeur
            </div>
            <div className="display text-xl mt-1">
              {isComplete ? "Profil complet — modifier mes informations" : "Complétez votre profil pour un devis rapide"}
            </div>
            <p className="text-sm text-zinc-700 mt-1">
              Sans adresse et téléphone à jour, les artisans ne peuvent pas se déplacer ni établir un devis précis.
              Vos coordonnées sont visibles uniquement par les artisans que vous choisissez.
            </p>
          </div>
        </div>
        {isComplete && (
          <button
            onClick={() => setOpen(false)}
            className="p-1 text-emerald-700 hover:text-emerald-900"
            data-testid="demandeur-profile-collapse"
            aria-label="Réduire"
          >
            <ChevronUp className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between mono text-[10px] uppercase tracking-widest text-zinc-600 mb-1.5">
          <span>Progression</span>
          <span data-testid="demandeur-profile-progress">{filledCount} / {total} · {percent}%</span>
        </div>
        <div className="h-2 bg-white border border-[#0A0A0B]">
          <div
            className={`h-full ${barColor} transition-all`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>

      {/* Checklist */}
      <ul className="mt-4 grid sm:grid-cols-2 gap-1.5">
        {items.map((it) => (
          <li key={it.key} className="flex items-center gap-2 text-sm">
            {it.filled ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-700 shrink-0" />
            ) : (
              <Circle className="w-4 h-4 text-zinc-400 shrink-0" />
            )}
            <span className={it.filled ? "text-emerald-800" : "text-zinc-700"}>{it.label}</span>
          </li>
        ))}
      </ul>

      {/* Form */}
      <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Nom complet *</span>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 text-sm focus:outline-none focus:bg-[#FFF7ED]"
            data-testid="demandeur-profile-name"
          />
        </label>
        <label className="block">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Téléphone *</span>
          <input
            type="tel"
            required
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="06 12 34 56 78"
            className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 text-sm focus:outline-none focus:bg-[#FFF7ED]"
            data-testid="demandeur-profile-phone"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Adresse (n° + rue) *</span>
          <input
            type="text"
            required
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="12 rue de la Paix"
            className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 text-sm focus:outline-none focus:bg-[#FFF7ED]"
            data-testid="demandeur-profile-address"
          />
        </label>
        <label className="block">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Code postal *</span>
          <input
            type="text"
            required
            value={form.postal_code}
            onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
            placeholder="75002"
            maxLength={10}
            className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 mono text-sm focus:outline-none focus:bg-[#FFF7ED]"
            data-testid="demandeur-profile-postal"
          />
        </label>
        <label className="block">
          <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">Ville *</span>
          <input
            type="text"
            required
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            placeholder="Paris"
            className="mt-1 w-full border-2 border-[#0A0A0B] bg-white px-3 py-2.5 text-sm focus:outline-none focus:bg-[#FFF7ED]"
            data-testid="demandeur-profile-city"
          />
        </label>
        <div className="sm:col-span-2 flex items-center gap-3 mt-1">
          <button
            type="submit"
            disabled={busy}
            className="btn-batisur flex items-center gap-2"
            data-testid="demandeur-profile-submit"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Enregistrement..." : "Enregistrer mon profil"}
          </button>
          {isComplete && (
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#0A0A0B]"
              data-testid="demandeur-profile-cancel"
            >
              Annuler
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
